import React, { useState, useEffect, useRef } from "react";
import "./styles.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://price1.dvvs-ekb.ru";


const CELL_HEIGHT = 195;
const TIME_BAR_TEXT_COLOR = "#fff";
const ACTIVITY_TEXT_COLOR = "#222";
const TIME_COLUMN_WIDTH = "60px";
const TIME_COLUMN_WIDTH_MOBILE = "40px";
const DAYS_IN_WEEK = 7;

const MONTHS_RU = [
  "ЯНВАРЯ","ФЕВРАЛЯ","МАРТА","АПРЕЛЯ","МАЯ","ИЮНЯ",
  "ИЮЛЯ","АВГУСТА","СЕНТЯБРЯ","ОКТЯБРЯ","НОЯБРЯ","ДЕКАБРЯ",
];
const WEEKDAYS_RU = [
  "ВОСКРЕСЕНЬЕ","ПОНЕДЕЛЬНИК","ВТОРНИК","СРЕДА","ЧЕТВЕРГ","ПЯТНИЦА","СУББОТА",
];

const times = [
  "09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00",
];

// Типы
type Activity = {
  date: string;
  time: string;
  name: string;
  trainer: string;
  room: string;
  spots: number;
  total: number;
  color?: string;
};
type DayCell = { date: string; jsdate: Date; isToday: boolean };


function pad(v: number | string) { return v.toString().padStart(2, "0"); }
function getMonday(d: Date) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  date.setDate(date.getDate() - ((day + 6) % 7));
  date.setHours(0, 0, 0, 0);
  return date;
}
function getWeekDays(startDate: Date): DayCell[] {
  const monday = getMonday(new Date(startDate));
  const res: DayCell[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    const yyyy = d.getFullYear(); const mm = pad(d.getMonth() + 1); const dd = pad(d.getDate());
    res.push({ date: `${yyyy}-${mm}-${dd}`, jsdate: d, isToday: isToday(d) });
  }
  return res;
}
function isToday(d: Date) {
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}
function formatDayLabel(d: { isToday: boolean; jsdate: Date }) {
  if (d.isToday) return <span className="fitness-grid-header-today">СЕГОДНЯ</span>;
  const day = pad(d.jsdate.getDate()); const month = MONTHS_RU[d.jsdate.getMonth()]; const weekday = WEEKDAYS_RU[d.jsdate.getDay()];
  return (
    <span>
      <div className="fitness-day-weekday">{weekday}</div>
      <span className="fitness-day-date">{day} {month}</span>
    </span>
  );
}
function formatFullDay(d: { jsdate: Date }) {
  const day = pad(d.jsdate.getDate()); const month = MONTHS_RU[d.jsdate.getMonth()]; const weekday = WEEKDAYS_RU[d.jsdate.getDay()];
  return `${day} ${month}, ${weekday}`;
}
function sortActivities(activities: Activity[]) {
  return [...activities].sort((a, b) => {
    const timeCmp = a.time.localeCompare(b.time, "ru"); if (timeCmp) return timeCmp;
    const nameCmp = a.name.localeCompare(b.name, "ru"); if (nameCmp) return nameCmp;
    const trainerCmp = a.trainer.localeCompare(b.trainer, "ru"); if (trainerCmp) return trainerCmp;
    return a.room.localeCompare(b.room, "ru");
  });
}
function getActivityBarColor(name: string) {
  if (
    /^Групповое занятие\s*"?Родитель \+ малыш"?/i.test(name) ||
    /^Групповое занятие в мини-группе \(взрослые\)/i.test(name) ||
    /^Групповое занятие в мини-группе \(дети\)/i.test(name)
  ) return "#29b6f6";
  if (/^Групповое занятие/i.test(name)) return "#713fe3";
  return "#29b6f6";
}
function getShortName(name: string) {
  if (/^Групповое занятие с инструктором/i.test(name)) {
    return name.replace(/^Групповое занятие с инструктором/i, "Групповое занятие");
  }
  return name;
}
function getClass(time: string, date: string) {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  const cellStart = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const cellEnd = new Date(cellStart.getTime() + 60 * 60 * 1000);

  const now = new Date();
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const cellDay = new Date(year, month - 1, day, 0, 0, 0, 0);

  if (cellDay < nowDay) return "past";
  if (cellDay > nowDay) return "";
  if (cellEnd <= now) return "past";
  return "";
}


function useMaxWidth(bp: number) {
  const [match, setMatch] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth <= bp : false);
  useEffect(() => {
    const handler = () => setMatch(window.innerWidth <= bp);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [bp]);
  return match;
}

export default function FitnessScheduleWidget() {
  const [startDate, setStartDate] = useState<Date>(() => getMonday(new Date()));
  const [schedule, setSchedule] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState<Date>(new Date());

  const days = getWeekDays(startDate);

  
  const isPhone = useMaxWidth(900);          // портрет/узкие экраны
  const hideTimeColumn = useMaxWidth(520);   // очень узкие — спрятать колонку времени
  const showTimeColumn = !hideTimeColumn;
  const visibleDaysCount = isPhone ? 2 : DAYS_IN_WEEK; // 2 дня на телефоне
  const [dayWindowStart, setDayWindowStart] = useState(0);
  const visibleDays = days.slice(dayWindowStart, dayWindowStart + visibleDaysCount);

  
  const touchStartX = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) {
      setDayWindowStart((s) => Math.min(s + 1, DAYS_IN_WEEK - visibleDaysCount));
    } else {
      setDayWindowStart((s) => Math.max(s - 1, 0));
    }
  }

  
  useEffect(() => {
    const todayIdx = days.findIndex((d) => d.isToday);
    if (isPhone && todayIdx >= 0) {
      const start = Math.max(0, Math.min(todayIdx, DAYS_IN_WEEK - visibleDaysCount));
      setDayWindowStart(start);
    } else {
      setDayWindowStart(0);
    }
    
  }, [startDate, isPhone]);

  
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  
  const [filterTime, setFilterTime] = useState("all");
  const [filterName, setFilterName] = useState("all");
  const [filterTrainer, setFilterTrainer] = useState("all");
  const [filterRoom, setFilterRoom] = useState("all");

  
  const [modal, setModal] = useState<{ open: boolean; activities: Activity[]; day: DayCell | null; time: string | null; }>({
    open: false, activities: [], day: null, time: null,
  });

  
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);
  function goToToday() {
    const mondayThisWeek = getMonday(new Date());
    setStartDate(mondayThisWeek);
    const tIdx = getWeekDays(mondayThisWeek).findIndex((d) => d.isToday);
    const start = isPhone ? Math.max(0, Math.min(tIdx, DAYS_IN_WEEK - visibleDaysCount)) : 0;
    if (tIdx >= 0) setDayWindowStart(start);
    setTimeout(() => {
      const today = getWeekDays(mondayThisWeek).find((d) => d.isToday);
      if (!today) return;
      const el = document.getElementById(`day-header-${today.date}`);
      el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, 50);
  }

  
  const uniqueNames = Array.from(new Set(schedule.map((a) => getShortName(a.name)))).sort();
  const uniqueTrainers = Array.from(new Set(schedule.map((a) => a.trainer))).sort();
  const uniqueRooms = Array.from(new Set(schedule.map((a) => a.room))).sort();

  function shiftWeek(dir: number) {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() + dir * 7);
    const thisMonday = getMonday(new Date());
    setStartDate(monday < thisMonday ? thisMonday : monday);
  }
  function filterByTime(time: string) {
    if (filterTime === "all") return true;
    const hour = parseInt(time.split(":")[0], 10);
    if (filterTime === "morning") return hour < 12;
    if (filterTime === "day") return hour >= 12 && hour < 17;
    if (filterTime === "evening") return hour >= 17;
    return true;
  }
  function filterActivity(activity: Activity) {
    return (
      (filterName === "all" || getShortName(activity.name) === filterName) &&
      (filterTrainer === "all" || activity.trainer === filterTrainer) &&
      (filterRoom === "all" || activity.room === filterRoom)
    );
  }

  
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load() {
      try {
        setLoading(true);
        const url = new URL("/api/schedule", API_BASE);
        url.searchParams.set("start", days[0].date);
        url.searchParams.set("end",   days[6].date);

        const res = await fetch(url.toString(), {
          method: "GET",
          headers: { "Accept": "application/json" },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Schedule fetch error: " + res.status);

        const json = await res.json();
        // Поддерживаем оба формата ответа: [ ... ] или { data: [ ... ] }
        const rows: any[] = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
        if (!cancelled) {
          setSchedule(rows);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Ошибка загрузки расписания из API:", err);
          setSchedule([]);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; controller.abort(); };
    
  }, [startDate]);

  const gridColumns =
    `${showTimeColumn ? (isPhone ? TIME_COLUMN_WIDTH_MOBILE : TIME_COLUMN_WIDTH) + " " : ""}` +
    `repeat(${visibleDaysCount}, minmax(${isPhone ? "160px" : "110px"}, 1fr))`;

  
  const nowHourStr = `${pad(now.getHours())}:00`;
  const nowMinutes = now.getMinutes();
  const isNowInRange = times.includes(nowHourStr);

  return (
    <div>
      <div className="fitness-widget">
        <div className="fitness-sticky-top">
          <div className="fitness-filters">
            <select value={filterTime} onChange={(e) => setFilterTime(e.target.value)}>
              <option value="all">Весь день</option>
              <option value="morning">Утро (до 12:00)</option>
              <option value="day">День (12:00–17:00)</option>
              <option value="evening">Вечер (после 17:00)</option>
            </select>
            <select value={filterName} onChange={(e) => setFilterName(e.target.value)}>
              <option value="all">Все занятия</option>
              {uniqueNames.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <select value={filterTrainer} onChange={(e) => setFilterTrainer(e.target.value)}>
              <option value="all">Все тренеры</option>
              {uniqueTrainers.map((trainer) => <option key={trainer} value={trainer}>{trainer}</option>)}
            </select>
            <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)}>
              <option value="all">Все бассейны</option>
              {uniqueRooms.map((room) => <option key={room} value={room}>{room}</option>)}
            </select>
          </div>

          <div className="fitness-header">
            <button
              className="fitness-nav-btn"
              onClick={() => shiftWeek(-1)}
              disabled={startDate.toISOString().slice(0, 10) === getMonday(new Date()).toISOString().slice(0, 10)}
              aria-label="Предыдущая неделя"
            >
              &lt;
            </button>
            <div className="fitness-header-title">
              {formatFullDay(days[0])} — {formatFullDay(days[6])}
            </div>
            <button className="fitness-nav-btn" onClick={() => shiftWeek(1)} aria-label="Следующая неделя">
              &gt;
            </button>
          </div>
        </div>

        <div
          className="fitness-grid-wrapper"
          ref={gridWrapperRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="fitness-grid" style={{ gridTemplateColumns: gridColumns }}>
            {/* Пустая ячейка слева наверху (или пропускаем, если нет колонки времени) */}
            {showTimeColumn ? <div className="fitness-grid-header-cell" /> : null}

            {}
            {visibleDays.map((day) => (
              <div
                key={day.date}
                id={`day-header-${day.date}`}
                className={`fitness-grid-header-cell${day.isToday ? " fitness-grid-header-today" : ""}`}
              >
                {formatDayLabel(day)}
              </div>
            ))}

            {}
            {times.filter(filterByTime).map((time) => (
              <React.Fragment key={time}>
                {showTimeColumn ? <div className="fitness-time-cell">{time}</div> : null}

                {visibleDays.map((day) => {
                  const cellClass = getClass(time, day.date);
                  const cellActivities = sortActivities(
                    schedule.filter((a) => a.date === day.date && a.time === time && filterActivity(a))
                  );

                  const showNowLine =
                    day.isToday && isNowInRange && time === nowHourStr;

                  return (
                    <div
                      key={`${time}_${day.date}`}
                      className={`fitness-cell ${cellClass}`}
                      style={{
                        height: CELL_HEIGHT, minHeight: CELL_HEIGHT, maxHeight: CELL_HEIGHT, position: "relative",
                      }}
                    >
                      {}
                      {showNowLine && (
                        <div
                          className="now-line"
                          style={{ top: (nowMinutes / 60) * CELL_HEIGHT }}
                          aria-hidden
                        />
                      )}

                      {cellActivities.length > 0 ? (
                        <>
                          <div
                            className="fitness-activity"
                            onClick={() => setModal({ open: true, activities: cellActivities, day, time })}
                            title="Показать все занятия"
                          >
                            <div
                              className="fitness-activity-timebar"
                              style={{ background: getActivityBarColor(cellActivities[0].name), color: TIME_BAR_TEXT_COLOR }}
                            >
                              {time}
                            </div>
                            <div className="fitness-act-name">{getShortName(cellActivities[0].name)}</div>
                            <div className="fitness-act-trainer">{cellActivities[0].trainer}</div>
                            <div className="fitness-act-room">{cellActivities[0].room}</div>
                            <div className="fitness-act-spots">
                              Мест: {cellActivities[0].spots}/{cellActivities[0].total}
                            </div>
                          </div>
                          {cellActivities.length > 1 && (
                            <div
                              className="fitness-cell-more"
                              onClick={() => setModal({ open: true, activities: cellActivities, day, time })}
                            >
                              +{cellActivities.length - 1}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="fitness-cell-empty" />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {loading && <div className="fitness-loading">Загрузка...</div>}
        {!loading && schedule.length === 0 && <div className="fitness-empty">Нет данных для отображения</div>}

        {modal.open && modal.day && modal.time && (
          <div
            className="fitness-modal-overlay"
            onClick={() => setModal({ open: false, activities: [], day: null, time: null })}
          >
            <div className="fitness-modal" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
              <button
                className="fitness-modal-close"
                onClick={() => setModal({ open: false, activities: [], day: null, time: null })}
                aria-label="Закрыть"
              >
                ×
              </button>
              <div className="fitness-modal-header">
                {formatFullDay(modal.day)}, {modal.time}
              </div>
              {sortActivities(modal.activities).map((a, i) => (
                <div className="fitness-activity" key={i}>
                  <div
                    className="fitness-activity-timebar"
                    style={{ background: getActivityBarColor(a.name), color: TIME_BAR_TEXT_COLOR }}
                  >
                    {modal.time}
                  </div>
                  <div className="fitness-act-name">{getShortName(a.name)}</div>
                  <div className="fitness-act-trainer">{a.trainer}</div>
                  <div className="fitness-act-room">{a.room}</div>
                  <div className="fitness-act-spots">Мест: {a.spots}/{a.total}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        <button className="floating-today-btn" onClick={goToToday} title="Перейти к сегодняшнему дню">
          Сегодня
        </button>
      </div>
    </div>
  );
}
